import { Component, OnInit, OnDestroy } from "@angular/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { FCM } from "@capacitor-community/fcm";
import { Subscription, Subject, BehaviorSubject } from "rxjs";
import { EventsService } from "src/app/services/event/event.service";
import { Platform } from "@ionic/angular";
import { UserSessionService } from "src/app/services/user-session/user-session.service";
import {
  distinctUntilChanged,
  map,
  filter,
  takeUntil,
  take,
} from "rxjs/operators";
import { WebservicesService } from "src/app/services/webservices/webservices.service";
import { ConfigService } from "src/app/services/config/config.service";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { NotificationHandlerService} from 'src/app/services/notification-handler/notification-handler.service'
import { RedirectService } from "src/app/services/redirect/redirect.service";


@Component({
  selector: "app-push-notification",
  templateUrl: "./push-notification.component.html",
  styleUrls: ["./push-notification.component.scss"],
})
export class PushNotificationComponent implements OnInit, OnDestroy {
  private customerId: string;
  private customerSubscription: Subscription | null = null;
  private messaging = null;
  private destroy$ = new Subject<void>();
  private pushNotificationListeners: any[] = [];
  private firebaseToken$: BehaviorSubject<string> = new BehaviorSubject<string>(
    null
  );

  constructor(
    private events: EventsService,
    private platform: Platform,
    private configService: ConfigService,
    private webservices: WebservicesService,
    private userSession: UserSessionService,
    private notificationHandler:NotificationHandlerService,
    private redirectService: RedirectService
  ) {
    this.listenToLogoutEvent();
    this.getCustomer();
  }

  ngOnInit(): void {
    this.platform.is("capacitor") ? this.initOnCapacitor() : this.initOnWeb();
  }

  private listenToLogoutEvent(): void {
    this.events.subscribe(
      "event:authentication:logout",
      () => (this.customerId = null)
    );
  }

  private async initOnWeb(): Promise<void> {
    if (this.configService.getConfig().FEATURE_FIREBASE_CONFIG) {
      const firebaseConfig = JSON.parse(
        this.configService.getConfig().FEATURE_FIREBASE_CONFIG
      );
      const app = initializeApp(firebaseConfig);
      Notification.requestPermission().then((permission) => {
        if (permission !== "granted") return;
        this.messaging = getMessaging(app);
        const webPublicKey =
          this.configService.getConfig().FEATURE_FIREBASE_PUBLICKEY;

        getToken(this.messaging, { vapidKey: webPublicKey })
          .then((currentToken) => {
            if (!currentToken) {
              console.log(
                "No registration token available. Request permission to generate one."
              );
              return;
            }
            this.firebaseToken$.next(currentToken);
            this.listen();
          })
          .catch((err) =>
            console.error("An error occurred while retrieving token.", err)
          );
      });
    }
  }

  private async initOnCapacitor(): Promise<void> {
    await PushNotifications.requestPermissions();
  await PushNotifications.register();

    FCM.getToken()
      .then((r) => this.firebaseToken$.next(r.token))
      .catch((err) => console.error(err));

    // Store the listener references correctly for cleanup
    const registrationListener = await PushNotifications.addListener("registration", (token) =>
      console.log("Push registration success, token:", token.value)
    );
    this.pushNotificationListeners.push(registrationListener);

    const registrationErrorListener = await PushNotifications.addListener("registrationError", (error) =>
      console.error("Error on registration:", error)
    );
    this.pushNotificationListeners.push(registrationErrorListener);

    const notificationReceivedListener = await PushNotifications.addListener("pushNotificationReceived", (notification) =>
      console.log("Push received:", notification)
    );
    this.pushNotificationListeners.push(notificationReceivedListener);

    const notificationActionListener = await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        console.log("Push action performed:", notification);
        this.handlePushNotificationAction(notification);
      }
    );
    this.pushNotificationListeners.push(notificationActionListener);
  }

  private handlePushNotificationAction(notification: any) {
    const clickAction = notification?.notification?.data?.click_action;
    if (clickAction) {
      switch (clickAction) {
        case "freecoins":{
          const promoCode = notification?.notification?.data?.promo_code;
          this.handleCampaignPromoCodesAction(clickAction,promoCode);
          break;
        }
        // Add more cases here for different click actions as needed
        default:
          console.warn("Unhandled click action:", clickAction);
      }
    }
  }

  private handleCampaignPromoCodesAction(clickAction: string, promoCode: string) {
    this.notificationHandler.isNotificationHalted = true;
    if (promoCode) {
      // Check current value of splash screen flag
      if (this.notificationHandler.isSplashScreenFinished) {
        // Process immediately
        setTimeout(() => {
          this.redirectService.redirectDeeplinks(clickAction, promoCode);
        }, 500);
      } else {
        // Wait for splash to complete
        const notificationHandlerSubscription = this.notificationHandler.isSplashScreenFinished$.subscribe(isFinished => {
          if (isFinished) {
            setTimeout(() => {
              this.redirectService.redirectDeeplinks(clickAction, promoCode);
              notificationHandlerSubscription.unsubscribe();
            }, 1000);
          }
        });
      }
    } else {
      this.notificationHandler.isNotificationHalted = false;
      console.warn("promoCode is missing, skipping redirect deeplinks call.");
    }
  }


  private getCustomer(): void {
    this.customerSubscription?.unsubscribe();
    this.userSession.getCustomerSubscription().then((obs) => {
      this.customerSubscription = obs
        .pipe(
          takeUntil(this.destroy$),
          filter((customer) => !!customer),
          map((customer) => customer.customerId),
          distinctUntilChanged()
        )
        .subscribe((customerId) => {
          if (customerId) {
            this.customerId = customerId.toString();
            this.tryRegisterDevice();
          }
        });
    });
  }
  private tryRegisterDevice(): void {
    this.firebaseToken$
      .pipe(
        filter((firebaseToken) => firebaseToken !== null),
        take(1)
      )
      .subscribe(async (firebaseToken) => {
        if (this.customerId && firebaseToken) {
          // Wait for device registration to complete
          console.log("will await for device registration");
          await this.registerDevice(firebaseToken);
          console.log("Device registration completed, triggering component reload");
          // Fire reload event immediately after registration is complete
          this.events.publish("event:push-notification:reload");
        } else {
          console.log(
            "Customer or Firebase Token is not available yet. Waiting..."
          );
        }
      });
  }
  private async registerDevice(firebaseToken): Promise<void> {
    const deviceType = this.getDeviceType();

    const registrationObject = {
      registration_token: firebaseToken,
      customer_id: this.customerId,
      device_type: deviceType,
      session_id: this.userSession.sessionToken,
      enabled: true,
    };
    await this.webservices.posttoURL(
      `${
        this.configService.getConfig().NOTIFICATION_BASEURL
      }/notificationapi/resources/push_notification/register_device`,
      registrationObject
    );
  }
  private listen(): void {
    if(!this.platform.is('capacitor'))return
    if (this.messaging) {
      onMessage(this.messaging, (payload) => {
        console.log("Message received. ", payload);
      });
    }
  }
  private getDeviceType(): string {
    if (!this.platform.is("capacitor")) return "WEB";
    if (this.platform.is("ios")) return "IOS";
    return "ANDROID";
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up push notification listeners properly
    if (this.pushNotificationListeners) {
      this.pushNotificationListeners.forEach(async (listener) => {
        if (listener && typeof listener.remove === 'function') {
          await listener.remove();
        }
      });
    }
  }
}
